import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { extractErr } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Brain, KeyRound, Loader2, Save, Sparkles } from "lucide-react";

export default function AdminAIStoreManager() {
  const [active, setActive] = useState(false);
  const [cfg, setCfg] = useState(null);
  const [key, setKey] = useState("");
  const [saving, setSaving] = useState(false);
  const [learning, setLearning] = useState(false);

  const load = () => api.get("/merchant/config/ai-store-manager").then(({ data }) => {
    setActive(Boolean(data.active));
    setCfg(data.ai_store_manager || {});
  }).catch((e) => toast.error(extractErr(e)));

  useEffect(() => { load(); }, []);

  if (!cfg) return <div className="text-slate-500">Loading…</div>;
  if (!active) {
    return (
      <div className="max-w-2xl space-y-4">
        <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> App Store</Link>
        <Card className="p-8 text-center text-slate-600">Unlock <strong>AI Store Manager</strong> from the App Store to run Grok on this store only.</Card>
      </div>
    );
  }

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        voice_enabled: Boolean(cfg.voice_enabled),
        voice_lang: cfg.voice_lang || "hi-IN",
        model: cfg.model || "grok-4.6",
      };
      if (key.trim()) payload.grok_api_key = key.trim();
      const { data } = await api.put("/merchant/config/ai-store-manager", payload);
      setCfg(data.ai_store_manager || {});
      setKey("");
      toast.success("AI Store Manager saved");
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setSaving(false);
    }
  };

  const learn = async () => {
    setLearning(true);
    try {
      const { data } = await api.post("/merchant/ai-store-manager/learn");
      setCfg((c) => ({ ...c, learned_at: data.learned_at, knowledge_summary: data.summary }));
      toast.success("Store memory updated");
    } catch (e) {
      toast.error(extractErr(e));
    } finally {
      setLearning(false);
    }
  };

  const learned = cfg.learned_at ? new Date(cfg.learned_at).toLocaleString("en-IN") : null;
  const langs = (cfg.languages && cfg.languages.length)
    ? cfg.languages
    : [
        { code: "en-IN", name: "English (India)", native: "English" },
        { code: "hi-IN", name: "Hindi", native: "हिन्दी" },
        { code: "ta-IN", name: "Tamil", native: "தமிழ்" },
        { code: "te-IN", name: "Telugu", native: "తెలుగు" },
        { code: "kn-IN", name: "Kannada", native: "ಕನ್ನಡ" },
        { code: "ml-IN", name: "Malayalam", native: "മലയാളം" },
        { code: "mr-IN", name: "Marathi", native: "मराठी" },
        { code: "gu-IN", name: "Gujarati", native: "ગુજરાતી" },
        { code: "bn-IN", name: "Bengali", native: "বাংলা" },
        { code: "pa-IN", name: "Punjabi", native: "ਪੰਜਾਬੀ" },
        { code: "or-IN", name: "Odia", native: "ଓଡ଼ିଆ" },
        { code: "as-IN", name: "Assamese", native: "অসমীয়া" },
        { code: "ur-IN", name: "Urdu", native: "اردو" },
      ];

  return (
    <div className="max-w-2xl space-y-5">
      <Link to="/admin/addons" className="text-sm text-slate-500 inline-flex items-center gap-1" data-testid="asm-back"><ArrowLeft className="w-4 h-4" /> App Store</Link>
      <div>
        <div className="inline-flex items-center gap-2 text-violet-700 text-xs font-semibold tracking-[0.2em] uppercase">
          <Sparkles className="w-3.5 h-3.5" /> Premium copilot
        </div>
        <h1 className="font-display text-3xl font-bold text-slate-900 mt-1">AI Store Manager</h1>
        <p className="text-sm text-slate-500 mt-1">Bring your Grok (xAI) API key. Grok’s top model works only on this store, understands Indian languages, and cannot unlock unpaid addons or touch another shop.</p>
      </div>

      <Card className="p-6 space-y-5 border-violet-200/70 bg-gradient-to-br from-white via-violet-50/40 to-indigo-50/30" data-testid="asm-keys-card">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-slate-900 text-violet-200 flex items-center justify-center">
            <KeyRound className="w-5 h-5" />
          </div>
          <div>
            <div className="font-semibold text-slate-900">Your Grok (xAI) API key</div>
            <div className="text-xs text-slate-500">{cfg.has_api_key ? "A key is stored for this store only." : "Paste a key from console.x.ai."}</div>
          </div>
        </div>
        <div>
          <Label htmlFor="asm-api-key">API key</Label>
          <Input id="asm-api-key" type="password" className="mt-1 font-mono" data-testid="asm-api-key" value={key} onChange={(e) => setKey(e.target.value)} placeholder={cfg.has_api_key ? "Saved — leave blank to keep" : "xai-…"} />
        </div>
        <div>
          <Label htmlFor="asm-voice-lang">Talk in</Label>
          <select
            id="asm-voice-lang"
            data-testid="asm-voice-lang"
            className="mt-1 w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm"
            value={cfg.voice_lang || "hi-IN"}
            onChange={(e) => setCfg({ ...cfg, voice_lang: e.target.value })}
          >
            {langs.map((l) => (
              <option key={l.code} value={l.code}>{l.native} — {l.name}</option>
            ))}
          </select>
          <p className="text-xs text-slate-500 mt-1">Grok understands Hindi, Tamil, Telugu, Kannada, Malayalam, Marathi, Gujarati, Bengali, Punjabi, Odia, Assamese, Urdu, and Indian English. Pick the language you will speak.</p>
        </div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-semibold text-slate-900">Voice</div>
            <p className="text-sm text-slate-500 mt-1">Listen and talk in the dashboard copilot using your browser’s Indian-language speech.</p>
          </div>
          <Switch checked={Boolean(cfg.voice_enabled)} onCheckedChange={(v) => setCfg({ ...cfg, voice_enabled: v })} data-testid="asm-voice" />
        </div>
        <Button type="button" onClick={save} disabled={saving} data-testid="asm-save" className="bg-slate-900 hover:bg-slate-800">
          <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
        </Button>
      </Card>

      <Card className="p-6 space-y-4 border-slate-200/70" data-testid="asm-learn-card">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-violet-600 text-white flex items-center justify-center">
            <Brain className="w-5 h-5" />
          </div>
          <div>
            <div className="font-semibold text-slate-900">Learn about my store</div>
            <p className="text-sm text-slate-500 mt-1">Index products, SEO, homepage, customers, and purchased addons so Grok can work with full context.</p>
          </div>
        </div>
        {cfg.knowledge_summary ? <p className="text-sm text-slate-700" data-testid="asm-knowledge-summary">{cfg.knowledge_summary}</p> : null}
        {learned ? <p className="text-xs text-slate-500">Last learned {learned}</p> : <p className="text-xs text-amber-700">Not learned yet — run this before handing over the store.</p>}
        <Button type="button" onClick={learn} disabled={learning || !cfg.has_api_key} data-testid="asm-learn" className="bg-violet-600 hover:bg-violet-500">
          {learning ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Brain className="w-4 h-4 mr-1" />}
          {learning ? "Learning your store…" : "Learn about my store"}
        </Button>
      </Card>
    </div>
  );
}
